import { Position, Positions } from '@agilox/ui-common';
import {
	Component,
	computed,
	effect,
	ElementRef,
	inject,
	model,
	OnInit,
	signal,
	TemplateRef,
	viewChild,
} from '@angular/core';
import { dropdownAnimation } from '../../animations/dropdown.animation';

const DROPDOWN_OFFSET = 4;
const MOBILE_APP_PADDING = 24;
const VIEWPORT_PADDING = 16;
const TOTAL_VIEWPORT_PADDING = VIEWPORT_PADDING * 2;
const MOBILE_BREAKPOINT = 768;

const DROPDOWN_TOP_POSITION_OFFSET = 32;
const DROPDOWN_BOTTOM_POSITION_OFFSET = 11;
const DROPDOWN_SIDE_POSITION_OFFSET = 8;
const DROPDOWN_HORIZONTAL_SPACING = 4;
const DROPDOWN_LEFT_POSITION_OFFSET = 1;

@Component({
	selector: 'ui-dropdown',
	templateUrl: './dropdown.component.html',
	animations: [dropdownAnimation],
	standalone: false,
})
export class DropdownComponent implements OnInit {
	width = model<number>(0);
	top = model<number>(0);
	minWidth = model<number>(0);
	left = model<number | undefined>(0);
	right = model<number | undefined>(undefined);
	bottom = model<number>(0);

	containerSelector = model<string>('.app');

	triggerTop = model<number>(0);
	triggerRight = model<number>(0);
	triggerLeft = model<number>(0);
	triggerBottom = model<number>(0);
	triggerWidth = model<number>(0);
	template = model<TemplateRef<any> | null>(null);
	id = model<string>('');
	openDropdownToTheLeft = model<boolean>(false);
	dropdownPosition = model<Position>(Positions.Bottom);

	dropdown = viewChild<ElementRef>('dropdown');

	open = signal<boolean>(false);

	public elementRef: ElementRef = inject(ElementRef);

	constructor() {
		effect(() => {
			this.triggerTop();
			this.triggerRight();
			this.triggerLeft();
			this.triggerBottom();
			this.triggerWidth();
			this.dropdownPosition();
			this.setPosition();
		});
	}

	ngOnInit() {
		/**
		 * Needed to trigger the dropdown animation
		 */
		setTimeout(() => {
			this.open.set(true);
			this.setPosition();
		});
	}

	close() {
		this.open.set(false);
	}

	/** Waits for the animation to complete
	 * Then checks if the dropdown exceeds the page height
	 * If it does, then the dropdown will be placed above the trigger,
	 * also checks the width of the dropdown and the width of the .app element
	 * We do not use the window.innerWidth, as we have a max width of our app
	 */
	setPosition() {
		if (this.dropdown()) {
			const { bottom, height, right, width, left } =
				this.dropdown()!.nativeElement.getBoundingClientRect();

			const appElement = document.querySelector(this.containerSelector());
			const app = appElement?.getBoundingClientRect();
			const appWidth = app?.right ?? window.innerWidth;
			const appPadding = window.innerWidth > MOBILE_BREAKPOINT ? 0 : MOBILE_APP_PADDING;

			if (this.dropdownPosition() === 'top') {
				this.bottom.set(window.innerHeight - this.triggerBottom() + DROPDOWN_TOP_POSITION_OFFSET);
				this.top.set(0);
			} else if (this.dropdownPosition() === 'bottom') {
				let finalTop = this.triggerBottom() + DROPDOWN_BOTTOM_POSITION_OFFSET;
				if (window.innerHeight < this.triggerBottom() + height + DROPDOWN_BOTTOM_POSITION_OFFSET) {
					finalTop = this.triggerTop() - height + DROPDOWN_OFFSET;
				}
				this.top.set(finalTop);
				this.bottom.set(0);
			} else if (this.dropdownPosition() === 'left' || this.dropdownPosition() === 'right') {
				this.top.set(this.triggerTop() + DROPDOWN_SIDE_POSITION_OFFSET);
				this.bottom.set(0);
			}

			if (this.dropdownPosition() === 'top' || this.dropdownPosition() === 'bottom') {
				if (
					this.openDropdownToTheLeft() ||
					(appWidth < this.triggerLeft() + width && this.triggerRight() - width >= (app?.left ?? 0))
				) {
					this.left.set(undefined);
					this.right.set(window.innerWidth - this.triggerRight());
				} else {
					this.left.set(
						this.triggerLeft() -
							(this.dropdownPosition() === 'top' ? DROPDOWN_LEFT_POSITION_OFFSET : 0)
					);
					this.right.set(undefined);
				}
			} else if (this.dropdownPosition() === 'left') {
				this.left.set(undefined);
				this.right.set(window.innerWidth - this.triggerLeft() + DROPDOWN_HORIZONTAL_SPACING);
			} else if (this.dropdownPosition() === 'right') {
				this.left.set(this.triggerRight() + DROPDOWN_HORIZONTAL_SPACING);
				this.right.set(undefined);
			}

			if (app && appWidth < width) {
				this.width.set(appWidth - appPadding);
				this.left.set((app.left ?? 0) + 1);
				this.right.set(undefined);
			}

			if (window.innerWidth < width + TOTAL_VIEWPORT_PADDING) {
				this.width.set(window.innerWidth - TOTAL_VIEWPORT_PADDING);
				this.left.set(VIEWPORT_PADDING);
				this.right.set(VIEWPORT_PADDING);
			}
		}
	}

	styles = computed(() => {
		const ret: any = {};
		if (this.top()) {
			ret['top'] = this.top() + 'px';
		}
		if (this.left() !== undefined) {
			ret['left'] = this.left() + 'px';
		}
		if (this.bottom()) {
			ret['bottom'] = this.bottom() + 'px';
		}
		if (this.minWidth()) {
			ret['min-width'] = this.minWidth() + 'px';
		}
		if (this.width()) {
			ret['width'] = this.width() + 'px';
		}
		if (this.right() !== undefined) {
			ret['right'] = this.right() + 'px';
		}

		return ret;
	});
}
